import type { ApolloError } from '@apollo/client';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import type { DesignLayoutType } from '@graphql/generated-base';
import { useEventWebsiteGuestQuery } from '../../guest/GuestSite.graphql.generated';
import { useGetRegistryDataLazyQuery } from './GuestRegistry.graphql.generated';
import { EventDesignPurpose, EventPageType, EventPageVisibility } from '@graphql/generated-base';
import { usePurchaseRegistryOrderMutation } from '@graphql/mutations/order.graphql.generated';
import { useCheckCanAccessPageQuery } from '@graphql/queries/event.graphql.generated';
import { useRegistryOrderByIdLazyQuery } from '@graphql/queries/registry.graphql.generated';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { fontMapping } from '@shared/utils/fonts/fontMap';
import { useRoutePaths } from './GuestRegistry.routes';
import { useLocation } from '@react-router';
import type * as H from 'history';
import type { ShippingAddressFragment } from '@graphql/aliases';
import { useEffect, useMemo, useState } from 'react';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { useGuestRegistryState } from './state';
import type { Theme } from '@withjoy/joykit';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { addError } from '@shared/utils/logger';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';
import { useEventLevelDateDisplay } from '@shared/utils/eventDateDisplay/useEventLevelDateDisplay';
import { removeItemFromPurchaseClickHistory } from '../common/components/ShoppingCart/utils/trackPurchaseClicks';
import { evictRegistryItemPurchaseCountsAfterPurchase } from '../common/utils/registryItemCache';
import type { GuestSiteEventDesign } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { useAppPage } from '@shared/utils/hooks/useAppPage';
import type { RegistryOrderList, RegistryList } from '../common/state/RegistryProducts';
import { deriveWebsiteColorsFromEventDesign } from '@shared/utils/websiteDesign';
import { withWindow } from '@shared/utils/withWindow';
import { isGuestLockedOut } from '@shared/utils/isGuestLockedOut';
import { useGenerateGuestSiteLoadTelemData } from '@apps/guest/routes/GuestSite/GuestSite.utils';
import { useGuestSiteUnlockState } from '@apps/guest/shared/hooks/useGuestSiteUnlockState';
import { useLayoutAndThemeOverride } from '@shared/hooks/useLayoutAndThemeOverride';
import { toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';

import { enrichTelemetryExtraInfo } from '@apps/guest/GuestSite.telemetry';
import { shouldUseEventDesignDraft } from './GuestRegistry.utils';
import { useGetGuestSitePages } from '@shared/hooks/useGetGuestSitePages';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';
import { PageKind } from '@graphql/generated-base';
import { loadWebFont } from '@apps/registry/common/utils/loadWebFont';
import { useWebsiteV2EventDesign } from '@shared/hooks/useEventWebsiteDesign/useWebsiteV2EventDesign';

export const DEFAULT_TIMEZONE = 'America/Los_Angeles';

export type GuestRegistryState = Readonly<{
  eventDate?: string;
  eventPhoto?: string;
  firebaseId?: string;
  isAdmin: boolean;
  pageNote?: string | null;
  registry: RegistryList;
  registryCurrencyCode?: string;
  orders: RegistryOrderList;
  registryAdminPhoto?: string;
  registryPhoto?: string;
  eventId?: string;
  primaryTextColor?: string;
  font?: string;
  shippingAddress: ShippingAddressFragment | null;
  isRegistryPageDisabled?: boolean;
  isRegistryPagePasswordProtected?: boolean;
  pages?: ReadonlyArray<EventPageFragment>;
  layout?: DesignLayoutType;
  eventDesign?: GuestSiteEventDesign;
  // PRNT-4193 — when set, admin photo edits on /registry route through
  // `uploadPhotoFromUrl` → `updatePageContainer` instead of legacy
  // `setEventPageAsset`. CoupleHeader passes this to <EventPhoto>.
  migratedPageContainerId?: string;
}>;

interface GuestRegistryProps
  extends Readonly<{
    loading: boolean;
    error?: ApolloError;
    registry: GuestRegistryState;
    routes: ReturnType<typeof useRoutePaths>;
    location: H.Location<H.LocationState>;
    canAccessPage?: boolean;
    eventId?: string;
    theme?: Theme;
  }> {}

export const useGuestRegistryController = (eventHandle: string): GuestRegistryProps => {
  const { role, hasIdentifiedUserOnce } = useEventUserRole();
  const { updateDataProvider, registry, orders } = useGuestRegistryState();
  const addAppPage = useAppPage();
  const { orderId: orderIdQueryParams, action: orderAction } = useQueryParams();

  const [hasEnrichedTelemetry, setHasEnrichedTelemetry] = useState(false);

  const {
    data: activeSessionData,
    loading: activeSessionLoading,
    error: activeSessionError,
  } = useCheckCanAccessPageQuery({
    batchMode: 'fast',
    variables: { eventHandle, type: EventPageType.registry },
  });
  const eventId = activeSessionData?.eventByName?.id;
  const firebaseId = activeSessionData?.eventByName?.firebaseId;

  const [getRegistryData, { loading, data, error }] = useGetRegistryDataLazyQuery({
    batchMode: 'fast',
    variables: { eventHandle, withGuestSiteData: true, eventDesignPurpose: shouldUseEventDesignDraft() ? EventDesignPurpose.draft : EventDesignPurpose.live },
  });
  // A Website V2 event's registry wears its site's design; read every design field off this one.
  const { eventDesign, loading: loadingWebsiteV2Design } = useWebsiteV2EventDesign(data?.eventByName?.eventDesign, eventHandle);
  const { pages: guestSitePages } = useGetGuestSitePages({ pages: data?.eventByName?.pages, layoutType: eventDesign?.websiteLayout.layoutType });
  const {
    getEventDesignWithThemeAndLayout,
    loadingThemeData,
    font: selectedFont,
    loadingFontPackData,
  } = useLayoutAndThemeOverride({
    ssr: false,
  });

  const routes = useRoutePaths();
  const location = useLocation();

  const canAccessPage = activeSessionData?.eventByName?.activeSession?.canAccessPage && !data;
  const registryPage = activeSessionData?.eventByName?.pageInfo;

  useEffect(() => {
    if (canAccessPage) {
      getRegistryData();
    }
  }, [getRegistryData, canAccessPage]);

  // Skip the synthetic "Get the app" page when locked out, so it doesn't become the only renderable page.
  const hasPasswordProtectedPage = (activeSessionData?.eventByName?.pagesInfo ?? []).some(p => p.visibility === EventPageVisibility.passwordProtected);
  const isLockedOut = isGuestLockedOut({
    hasAccess: activeSessionData?.eventByName?.activeSession?.canAccessPage,
    pageCount: data?.eventByName?.pages?.length ?? 0,
    hasPasswordProtectedPage,
  });
  const pages = useMemo(
    () => (isLockedOut ? guestSitePages : addAppPage(eventHandle, data?.eventByName)),
    [addAppPage, data?.eventByName, eventHandle, guestSitePages, isLockedOut],
  );

  const info = data?.eventByName?.info;
  const { formatDate } = useEventLocaleFormatter();
  const formattedDate = formatDate(info?.finalizedDate?.timestamp, 'weekday, dd MMMM yyyy', {
    titlecase: true,
    timezone: info?.finalizedDate?.timezone,
    isoTimestamp: info?.finalizedDate?.timestamp,
  });
  // PRNT-4642 — multi-day range from event-level EventInfo; undefined without a saved range, so `eventDate`
  // keeps the single date. Range start is the dedicated startDate override, NOT finalizedDate (a date *within*
  // the event → wrong range). Flag-gated behind websiteMultiDayEventDateEnabled + additive.
  const eventDateDisplay = useEventLevelDateDisplay({
    startTimestamp: info?.startDate?.timestamp,
    timezone: info?.startDate?.timezone,
    dateMetadata: info?.dateMetadata,
    endDateTimestamp: info?.endDate?.timestamp,
  });
  let primaryTextColor: string | undefined = undefined;
  if (eventDesign) {
    primaryTextColor = deriveWebsiteColorsFromEventDesign(eventDesign, eventDesign.theme).pageFontColor;
  }
  const font = typeof selectedFont === 'string' ? selectedFont : eventDesign?.font?.fontFamily;
  const registryShippingAddress = data?.eventByName?.registry?.shippingAddress || null;

  let mappedFont = font;
  if (font && mappedFont) {
    if (fontMapping[font]) {
      mappedFont = fontMapping[font];
    }
  }

  useEffect(() => {
    // Check if window is defined to ensure code runs only in the client side
    withWindow(() => {
      loadWebFont(mappedFont || '');
    });
  }, [font, mappedFont]);

  const isAdmin = role === 'superAdmin' || role === 'admin' || role === 'owner';
  // PRNT-4193 — flag + data gate matches `RegistryLinksEditorRoute` so admin
  // photo edits on /registry follow the same routing rule as the website-editor.
  const registryPageContainer = data?.registryPageContainer;
  const isMigratedRegistry = registryPageContainer?.pageKind === PageKind.systemCustom;
  const migratedPageContainerId = isMigratedRegistry ? registryPageContainer?.id : undefined;
  // With `useRegistryLinksAsCustomPage` on, event_service filters the legacy `type=registry`
  // row out of `pages(admin: true)` — the migrated `type=custom, pageSlug=registrylinks`
  // synthetic row carries the welcome-note + photo via the PageContainer factory. Match
  // either so flag-on events still surface their note/photo on /registry.
  const registryPageWithGuestInfo = guestSitePages.find(
    item => item.type === EventPageType.registry || (item.type === EventPageType.custom && toCanonicalMigratedSlug(item.pageSlug) === EVENT_PAGE_SLUGS.Registry),
  );

  const photo = data?.eventByName?.welcomePage[0]?.photoV2 || registryPageWithGuestInfo?.photoV2;
  const eventPhoto = photo?.url || '';

  const modifiedEventDesign = useMemo(() => {
    return eventDesign ? getEventDesignWithThemeAndLayout(eventDesign) : eventDesign;
  }, [eventDesign, getEventDesignWithThemeAndLayout]);

  const guestRegistry = {
    coupleDisplayName: info?.eventDisplayName,
    eventDate: eventDateDisplay ?? formattedDate,
    eventPhoto,
    firebaseId,
    eventId,
    primaryTextColor,
    isAdmin,
    pageNote: registryPageWithGuestInfo?.subTitle,
    registry,
    registryCurrencyCode: data?.eventByName?.registry?.currency?.code,
    orders,
    registryAdminPhoto: registryPageWithGuestInfo?.photoV2?.url,
    font: mappedFont,
    shippingAddress: registryShippingAddress,
    isRegistryPageDisabled: registryPage?.disabled,
    isRegistryPagePasswordProtected: registryPage?.private,
    pages,
    layout: eventDesign?.websiteLayout.layoutType,
    eventDesign: modifiedEventDesign,
    migratedPageContainerId,
  };

  const getTypeFromOrderAction = (orderAction: string) => {
    switch (orderAction) {
      case 'undo':
        return 'undo';
      case 'purchase':
        return 'initial';
      case 'confirm':
        return 'purchased';
      default:
        return '';
    }
  };

  const [purchaseRegistryOrder] = usePurchaseRegistryOrderMutation();
  const { toast } = useToast();
  const { tq } = useTranslation('sharedRegistry');

  const [getRegistryOrderData] = useRegistryOrderByIdLazyQuery({
    variables: {
      id: orderIdQueryParams as string,
    },
    batchMode: 'fast',
    onCompleted: data => {
      const registryOrder = data.registryOrderById;

      if (registryOrder) {
        const { lineItems, name, email } = registryOrder;
        const donationFundPlatformType = registryOrder?.lineItems?.[0]?.lineItemData?.donationFundPlatformType || null;
        const registryItem = lineItems[0];
        const orderType = getTypeFromOrderAction(orderAction as string);
        const productId = registryItem?.registryItem?.id;
        const purchasedItemIds = lineItems.map(lineItem => lineItem?.registryItem?.id).filter((id): id is string => Boolean(id));
        if (orderType === 'purchased') {
          purchaseRegistryOrder({
            variables: { id: registryOrder.id },
            update: cache => evictRegistryItemPurchaseCountsAfterPurchase({ cache, registryItemIds: purchasedItemIds }),
          }).catch(err => {
            addError(err, { context: 'GuestRegistry.purchaseRegistryOrder', orderId: registryOrder.id });
            toast(tq`Something went wrong, try again later`);
          });
          if (productId) {
            removeItemFromPurchaseClickHistory({ reservedOrderId: registryOrder.id, productId });
          }
        }
        updateDataProvider({
          type: orderType,
          productId,
          order: {
            orderId: orderIdQueryParams as string,
            donationFundPlatformType: donationFundPlatformType!,
          },
          guest: {
            name,
            email,
          },
          quantity: Math.max(registryItem?.quantity ? registryItem.quantity / 100 : 0, 0),
        });
      } else {
        updateDataProvider({
          type: 'error',
          order: null,
          guest: null,
          quantity: 0,
        });
      }
    },
    onError: () => {
      updateDataProvider({
        type: 'error',
        order: null,
        guest: null,
        quantity: 0,
      });
    },
  });

  useEffect(() => {
    if (orderIdQueryParams) {
      getRegistryOrderData();
    }
  }, [orderIdQueryParams, getRegistryOrderData]);

  const { data: guestSiteLoadTelemData, loading: guestSiteLoadTelemDataLoading } = useEventWebsiteGuestQuery({
    variables: {
      eventHandle,
      eventDesignPurpose: EventDesignPurpose.live,
      isAdminDashboard: isAdmin,
    },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    skip: !hasIdentifiedUserOnce,
  });

  // PLAN-17 step-2: hasUnlocked no longer rides on EventWebsiteGuest.
  const { hasUnlocked, isUnlockStateLoading } = useGuestSiteUnlockState(eventHandle);
  const extraInfo = useGenerateGuestSiteLoadTelemData(guestSiteLoadTelemData?.eventByName, role, hasUnlocked);

  // Enrich all page view events with GuestSiteLoad properties to eliminate redundant GuestSiteLoad track event
  // No race condition: guestSiteLoadTelemDataLoading is included in the loading state,
  // so the registry won't render and page tracking won't start until enrichment completes
  useEffect(() => {
    // Also wait for the unlock query: enrichment runs once, and hasUnlocked feeds
    // security.isUnlocked — enriching while it's undefined would drop that field.
    if (!guestSiteLoadTelemDataLoading && !isUnlockStateLoading && guestSiteLoadTelemData && !hasEnrichedTelemetry) {
      enrichTelemetryExtraInfo({ ...extraInfo, guestSiteLoad: true });
      setHasEnrichedTelemetry(true);
    }
  }, [guestSiteLoadTelemDataLoading, isUnlockStateLoading, guestSiteLoadTelemData, setHasEnrichedTelemetry, extraInfo, hasEnrichedTelemetry]);

  return {
    error: error || activeSessionError,
    loading:
      loading || activeSessionLoading || loadingThemeData || loadingFontPackData || loadingWebsiteV2Design || guestSiteLoadTelemDataLoading || (!data && !error && !!canAccessPage),
    registry: guestRegistry,
    routes,
    location,
    canAccessPage: activeSessionData?.eventByName?.activeSession?.canAccessPage,
    eventId: activeSessionData?.eventByName?.id,
  };
};
